// no-dd-sa:typescript-best-practices/boolean-prop-naming
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Zap, ChevronDown } from 'lucide-react';
import SkipLink from '@/components/a11y/SkipLink';
import BrandName from '@/components/common/BrandName';
import ProductSwitcher, { REVIEWS_URL } from '@/components/common/ProductSwitcher';
import FeaturesMegaMenu from '@/components/landing/FeaturesMegaMenu';
import { useGitHubLogin } from '@/hooks/useGitHubLogin';
import { useAuthStore } from '@/store/authStore';
import { useSubscriptionStore } from '@/store/subscriptionStore';
import { FEATURE_CATEGORIES, getFeatureCatalogGrouped } from '@/data/featureCatalog';

// Computed once at module level since the catalog is static
const mobileFeatureGroups = getFeatureCatalogGrouped();

interface LandingHeaderProps {
  /** Force the scrolled (dark text) style, useful for pages with light backgrounds */
  forceDarkText?: boolean;
}

export default function LandingHeader({ forceDarkText = false }: LandingHeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileFeaturesOpen, setIsMobileFeaturesOpen] = useState(false);
  const { isLoading, handleGitHubLogin } = useGitHubLogin();
  const { isAuthenticated } = useAuthStore();
  const { subscription } = useSubscriptionStore();
  const showUpgrade = isAuthenticated && (!subscription || subscription.effective_tier === 'free' || subscription.is_trial_active);

  // Use scrolled styling if actually scrolled OR if forceDarkText is set
  const useDarkText = isScrolled || forceDarkText;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        useDarkText
          ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <SkipLink />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/logo.svg"
              alt="CodePulse Logo"
              className="w-9 h-9"
              aria-hidden="true"
            />
            <BrandName className={`text-xl font-bold ${useDarkText ? 'text-gray-900 dark:text-white' : 'text-white'}`} />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            <FeaturesMegaMenu useDarkText={useDarkText} />
            <Link
              to="/how-it-works"
              className={`text-sm font-medium transition-colors ${
                useDarkText ? 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white' : 'text-white/80 hover:text-white'
              }`}
            >
              How It Works
            </Link>
            <Link
              to="/stories"
              className={`text-sm font-medium transition-colors ${
                useDarkText ? 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white' : 'text-white/80 hover:text-white'
              }`}
            >
              Stories
            </Link>
            <Link
              to="/security"
              className={`text-sm font-medium transition-colors ${
                useDarkText ? 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white' : 'text-white/80 hover:text-white'
              }`}
            >
              Security
            </Link>
            <Link
              to="/pricing"
              className={`text-sm font-medium transition-colors ${
                useDarkText ? 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white' : 'text-white/80 hover:text-white'
              }`}
            >
              Pricing
            </Link>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            <ProductSwitcher current="analytics" useDarkText={useDarkText} />
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                {showUpgrade && (
                  <Link
                    to="/pricing"
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      useDarkText
                        ? 'bg-amber-500 text-white hover:bg-amber-600'
                        : 'bg-amber-400 text-gray-900 hover:bg-amber-300'
                    }`}
                  >
                    <Zap className="w-3.5 h-3.5" />
                    Upgrade
                  </Link>
                )}
                <Link
                  to="/dashboard"
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    useDarkText
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-white text-primary-600 hover:bg-white/90'
                  }`}
                >
                  Go to Dashboard
                </Link>
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`text-sm font-medium transition-colors ${
                    useDarkText ? 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white' : 'text-white/80 hover:text-white'
                  }`}
                >
                  Sign In
                </Link>
                <button
                  onClick={() => handleGitHubLogin('header')}
                  disabled={isLoading}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    useDarkText
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-white text-primary-600 hover:bg-white/90'
                  } disabled:opacity-50`}
                >
                  {isLoading ? 'Connecting...' : 'Find Your #1 Bottleneck'}
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${useDarkText ? 'text-gray-900 dark:text-white' : 'text-white'}`} aria-hidden="true" />
            ) : (
              <Menu className={`w-6 h-6 ${useDarkText ? 'text-gray-900 dark:text-white' : 'text-white'}`} aria-hidden="true" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white dark:bg-gray-800 rounded-lg shadow-lg mt-2 p-4" id="mobile-menu">
            <nav className="flex flex-col gap-4" aria-label="Mobile navigation">
              {/* Mobile Features accordion */}
              <div>
                <button
                  className="flex items-center justify-between w-full text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium"
                  onClick={() => setIsMobileFeaturesOpen((prev) => !prev)}
                  aria-expanded={isMobileFeaturesOpen}
                >
                  Features
                  <ChevronDown className={`w-4 h-4 transition-transform ${isMobileFeaturesOpen ? 'rotate-180' : ''}`} />
                </button>
                {isMobileFeaturesOpen && (
                  <div className="mt-2 ml-2 space-y-3">
                    {(Object.keys(FEATURE_CATEGORIES) as Array<keyof typeof FEATURE_CATEGORIES>).map((cat) => {
                      const group = mobileFeatureGroups[cat];
                      return (
                        <div key={cat}>
                          <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">
                            {FEATURE_CATEGORIES[cat].label}
                          </p>
                          {group.map((feature) => (
                            <Link
                              key={feature.slug}
                              to={`/features/${feature.slug}`}
                              className="block py-1 text-sm text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400"
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {feature.title}
                            </Link>
                          ))}
                        </div>
                      );
                    })}
                    <Link
                      to="/features"
                      className="block text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 pt-1"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      See all features
                    </Link>
                  </div>
                )}
              </div>
              <a
                href="/#how-it-works"
                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                How It Works
              </a>
              <Link
                to="/stories"
                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Stories
              </Link>
              <Link
                to="/security"
                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Security
              </Link>
              <Link
                to="/pricing"
                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Pricing
              </Link>
              <a
                href={REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#f97316' }} />
                CodePulse Reviews
              </a>
              <hr className="border-gray-200 dark:border-gray-700" />
              {isAuthenticated ? (
                <>
                  {showUpgrade && (
                    <Link
                      to="/pricing"
                      className="inline-flex items-center justify-center gap-1.5 w-full px-4 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Zap className="w-3.5 h-3.5" />
                      Upgrade
                    </Link>
                  )}
                  <Link
                    to="/dashboard"
                    className="btn btn-primary w-full text-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                >
                  Go to Dashboard
                </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium"
                  >
                    Sign In
                  </Link>
                  <button
                    onClick={() => handleGitHubLogin('header_mobile')}
                    disabled={isLoading}
                    className="btn btn-primary w-full"
                  >
                    {isLoading ? 'Connecting...' : 'Find Your #1 Bottleneck'}
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}